import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import debounce from 'lodash.debounce';

import { actions as treeActions } from 'features/tree';
import { actions } from 'features/reports/redux';
import { actions as usersActions } from 'features/users/redux';

import {
  diffRangeItems,
  generalReportDaysFilterValue,
  generalReportMetrics,
  providerScheduleMetrics,
  structure,
} from '../data';
import { buildSavedFilterLabels } from '../lib/buildSavedFilterLabels';
import useActiveUsersStatistics from './useActiveUsersStatistics';

const defaultDay = generalReportDaysFilterValue.find(item => item.text === 'last7Days');
const defaultDiffRange = diffRangeItems.last7Days[0];

const getPreviousPeriod = date => {
  const begin = dayjs(date.beginDate).startOf('day');
  const end = dayjs(date.endDate).startOf('day');
  const duration = Math.max(end.diff(begin, 'day'), 0);
  const compareTo = begin.subtract(1, 'day');

  return {
    beginDate: compareTo.subtract(duration, 'day').toDate(),
    endDate: compareTo.toDate(),
  };
};

const useGeneralModel = () => {
  const { selectedAgentId } = useSelector(state => state.tree, shallowEqual);
  const locale = useSelector(state => state.locale.locale);
  const dispatch = useDispatch();

  const [date, setDate] = useState({
    beginDate: defaultDay.value,
    endDate: defaultDay.value2,
  });
  const [diffDate, setDiffDate] = useState(() => getPreviousPeriod({
    beginDate: defaultDay.value,
    endDate: defaultDay.value2,
  }));
  const [withDiff, setWithDiff] = useState(false);
  const [activeDay, setActiveDay] = useState(defaultDay);
  const [activeDiffRange, setActiveDiffRange] = useState(defaultDiffRange);
  const [filters, setFilters] = useState({ structure: structure[0] });
  const [activeMetric, setActiveMetric] = useState('incomeAmount');
  const [isScheduleOpen, setIsScheduleOpen] = useState(false);
  const [expandedProvider, setExpandedProvider] = useState('');
  const [isActiveUsersEnabled, setIsActiveUsersEnabled] = useState(false);
  const [providerMetrics, setProviderMetrics] = useState([]);
  const [isProviderScheduleOpen, setIsProviderScheduleOpen] = useState(false);
  const [providerMetricError, setProviderMetricError] = useState(false);
  const [isSaveFilterOpen, setIsSaveFilterOpen] = useState(false);

  const [filterValue, setFilterValue] = useState('');
  const [searchResult, setSearchResult] = useState([]);
  const [isLoadingSearchResult, setIsLoadingSearchResult] = useState(false);

  const getDiffDate = useCallback(() => {
    if (activeDiffRange.name === 'manualCompare') {
      return {
        compareFrom: diffDate.beginDate,
        compareTo: diffDate.endDate,
      };
    }

    const begin = dayjs(date.beginDate).startOf('day');
    const end = dayjs(date.endDate).startOf('day');
    const { count = 0, type = 'day' } = activeDiffRange.value || {};
    const compareFrom = begin.subtract(count, type);

    return {
      compareFrom: compareFrom.toDate(),
      compareTo: compareFrom.add(end.diff(begin, 'day'), 'day').toDate(),
    };
  }, [activeDiffRange, date.beginDate, date.endDate, diffDate.beginDate, diffDate.endDate]);

  const activeUsers = useActiveUsersStatistics(
    { date, ...filters, withDiff, diffDate: getDiffDate() },
    isActiveUsersEnabled || Boolean(expandedProvider),
    isActiveUsersEnabled && activeMetric === 'activeUsers' && isScheduleOpen && !isProviderScheduleOpen,
  );

  const handleActiveUsersToggle = () => {
    if (activeUsers.isLoading) return;
    if (!isActiveUsersEnabled) activeUsers.reload();
    setIsActiveUsersEnabled(current => !current);
    if (isActiveUsersEnabled && activeMetric === 'activeUsers') setActiveMetric('incomeAmount');
  };

  const debouncedSearchUser = useRef(
    debounce((value, isDirectStructure) => {
      if (value.length >= 4) {
        setIsLoadingSearchResult(true);
        dispatch(
          usersActions.getUsersListForSearch({
            username: value,
            isDirectStructure,
            callback: data => {
              if (data) setSearchResult(data.map(item => item.username));
              setIsLoadingSearchResult(false);
            },
          }),
        );
      }
    }, 800),
  ).current;

  const handleStructureChange = value => {
    const structureItem = structure.find(item => item.value === value);
    if (structureItem) setFilters({ structure: structureItem });
  };

  const handleSearchChange = event => {
    const { value } = event.target;
    setFilterValue(value);

    if (value.length >= 4) {
      setIsLoadingSearchResult(true);
      debouncedSearchUser(value, filters.structure.value === 'directOnly');
    } else {
      setIsLoadingSearchResult(false);
      setSearchResult([]);
    }
  };

  const handleResultClick = username => {
    setFilterValue(username);
    setIsLoadingSearchResult(false);
    setSearchResult([]);
  };

  const updateCurrentDate = nextDate => {
    setDate(nextDate);
    setDiffDate(getPreviousPeriod(nextDate));
    setActiveDay({});
    setActiveDiffRange({ name: 'manualCompare', value: 'manualCompare' });
  };

  const onChangeBeginDate = value => {
    if (!isNaN(value.valueOf())) updateCurrentDate({ ...date, beginDate: value });
  };

  const onChangeEndDate = value => {
    if (!isNaN(value.valueOf())) updateCurrentDate({ ...date, endDate: value });
  };

  const onChangeBeginDiffDate = value => {
    if (!isNaN(value.valueOf())) setDiffDate(previous => ({ ...previous, beginDate: value }));
    setActiveDiffRange({ name: 'manualCompare', value: 'manualCompare' });
  };

  const onChangeEndDiffDate = value => {
    if (!isNaN(value.valueOf())) setDiffDate(previous => ({ ...previous, endDate: value }));
    setActiveDiffRange({ name: 'manualCompare', value: 'manualCompare' });
  };

  const onChangeActiveDay = useCallback(dayFilter => {
    setActiveDay(dayFilter);
    setDate({ beginDate: dayFilter.value, endDate: dayFilter.value2 });
    const nextDiffRange = diffRangeItems[dayFilter.text]?.[0] || defaultDiffRange;
    setActiveDiffRange(nextDiffRange);
  }, []);

  const onChangeDiffRange = useCallback((dayFilter, name) => {
    setActiveDiffRange({ value: dayFilter.value, name });
  }, []);

  const handleSearch = () => {
    dispatch(treeActions.setSearchTree(filterValue));
    setSearchResult([]);
  };

  const handleFormSubmit = event => {
    event.preventDefault();
    handleSearch();
  };

  const handleKeyDown = event => {
    if (event?.key === 'Enter') {
      event.preventDefault();
      handleSearch();
    }
  };

  const handleProviderToggle = provider => {
    if (activeUsers.isLoading) return;
    if (expandedProvider !== provider) activeUsers.reload();
    dispatch(actions.clearProviderDetails());
    setExpandedProvider(current => (current === provider ? '' : provider));
    setIsProviderScheduleOpen(false);
    setProviderMetricError(false);
  };

  const handleProviderScheduleOpen = () => {
    if (!providerMetrics.length) {
      setProviderMetricError(true);
      setIsProviderScheduleOpen(false);
      return;
    }
    setProviderMetricError(false);
    setIsProviderScheduleOpen(true);
    setIsScheduleOpen(true);
  };

  const handleProviderMetricsApply = metrics => {
    setProviderMetrics(metrics);
    if (!metrics.length) {
      setProviderMetricError(true);
      setIsProviderScheduleOpen(false);
      return;
    }
    setProviderMetricError(false);
    setIsProviderScheduleOpen(true);
    setIsScheduleOpen(true);
  };

  const handleSetActiveMetric = metric => {
    if (metric === 'activeUsers' && (!isActiveUsersEnabled || activeUsers.isLoading)) return;
    setActiveMetric(metric);
    setIsProviderScheduleOpen(false);
  };

  const savedFilterParams = useMemo(
    () => ({
      version: 1,
      date_from: dayjs(date.beginDate).format('YYYY-MM-DD'),
      date_to: dayjs(date.endDate).format('YYYY-MM-DD'),
      quick_period: activeDay.text || null,
      structure: filters.structure.value,
      agent_id: selectedAgentId,
      agent_search: filterValue,
      metric: activeMetric,
      graph_open: isScheduleOpen,
      compare: {
        enabled: withDiff,
        date_from: dayjs(diffDate.beginDate).format('YYYY-MM-DD'),
        date_to: dayjs(diffDate.endDate).format('YYYY-MM-DD'),
        range_name: activeDiffRange.name,
        range_value: activeDiffRange.value,
      },
      providers: expandedProvider ? [expandedProvider] : [],
      provider_metrics: providerMetrics,
      provider_graph_open: isProviderScheduleOpen,
    }),
    [
      activeDay.text,
      activeDiffRange,
      activeMetric,
      date.beginDate,
      date.endDate,
      diffDate.beginDate,
      diffDate.endDate,
      expandedProvider,
      filterValue,
      filters.structure.value,
      isProviderScheduleOpen,
      isScheduleOpen,
      providerMetrics,
      selectedAgentId,
      withDiff,
    ],
  );

  const handleSaveFilter = callback => {
    const { name, description } = buildSavedFilterLabels(savedFilterParams, locale);

    dispatch(
      actions.createSavedFilter(
        {
          name,
          description,
          params: savedFilterParams,
        },
        callback,
      ),
    );
  };

  const handleApplySavedFilter = savedFilter => {
    const params = savedFilter.params || {};
    if (!params.date_from || !params.date_to) return;
    const restoredDate = {
      beginDate: dayjs(params.date_from).toDate(),
      endDate: dayjs(params.date_to).toDate(),
    };
    const restoredQuickPeriod = generalReportDaysFilterValue.find(item => item.text === params.quick_period) || {};
    const restoredStructure = structure.find(item => item.value === params.structure) || structure[0];
    const restoredMetric = generalReportMetrics.some(item => item.key === params.metric)
      ? params.metric
      : 'incomeAmount';
    const restoredProviderMetrics = (params.provider_metrics || []).filter(metric =>
      providerScheduleMetrics.some(item => item.key === metric),
    );

    setDate(restoredDate);
    setActiveDay(restoredQuickPeriod);
    setFilters({ structure: restoredStructure });
    setFilterValue(params.agent_search || '');
    setActiveMetric(restoredMetric === 'activeUsers' && !isActiveUsersEnabled ? 'incomeAmount' : restoredMetric);
    setIsScheduleOpen(Boolean(params.graph_open || (params.provider_graph_open && restoredProviderMetrics.length)));
    setWithDiff(Boolean(params.compare?.enabled));
    setDiffDate({
      beginDate: dayjs(params.compare?.date_from || getPreviousPeriod(restoredDate).beginDate).toDate(),
      endDate: dayjs(params.compare?.date_to || getPreviousPeriod(restoredDate).endDate).toDate(),
    });
    setActiveDiffRange({
      name: params.compare?.range_name || 'manualCompare',
      value: params.compare?.range_value || 'manualCompare',
    });
    if (!activeUsers.isLoading) {
      dispatch(actions.clearProviderDetails());
      setExpandedProvider(params.providers?.[0] || '');
    }
    setProviderMetrics(restoredProviderMetrics);
    setIsProviderScheduleOpen(Boolean(params.provider_graph_open && restoredProviderMetrics.length));

    if (params.agent_id) dispatch(treeActions.selectAgentId(params.agent_id));
  };

  useEffect(() => {
    if (filterValue.length < 4) setSearchResult([]);
  }, [filterValue]);

  useEffect(() => {
    dispatch(treeActions.setSearchTree(''));
    dispatch(actions.getSavedFilters());
  }, [dispatch]);

  useEffect(() => {
    if (activeDiffRange.name !== 'manualCompare') {
      const begin = dayjs(date.beginDate).startOf('day');
      const end = dayjs(date.endDate).startOf('day');
      const { count = 0, type = 'day' } = activeDiffRange.value || {};
      const compareFrom = begin.subtract(count, type);
      setDiffDate({
        beginDate: compareFrom.toDate(),
        endDate: compareFrom.add(end.diff(begin, 'day'), 'day').toDate(),
      });
    }
  }, [activeDiffRange, date.beginDate, date.endDate]);

  useEffect(() => {
    if (!selectedAgentId) return;
    dispatch(
      actions.getStatisticProvider({
        date,
        ...filters,
        withDiff,
        diffDate: getDiffDate(),
      }),
    );
  }, [
    date.beginDate,
    date.endDate,
    diffDate.beginDate,
    diffDate.endDate,
    filters.structure,
    getDiffDate,
    selectedAgentId,
    withDiff,
    dispatch,
  ]);

  useEffect(() => {
    if (!selectedAgentId) return;
    if (activeMetric === 'activeUsers') return;
    const metric = generalReportMetrics.find(item => item.key === activeMetric)?.metric;
    if (!metric) return;
    dispatch(actions.getReportTimeseries({ date, diffDate: getDiffDate(), metric }));
  }, [activeMetric, date, dispatch, getDiffDate, selectedAgentId]);

  useEffect(() => {
    if (!expandedProvider || !selectedAgentId) return;
    dispatch(actions.getProviderRanks({ provider: expandedProvider, date }));
  }, [date, dispatch, expandedProvider, filters.structure, selectedAgentId, withDiff]);

  useEffect(() => {
    if (!isProviderScheduleOpen || !expandedProvider || !providerMetrics.length || !selectedAgentId) return;
    dispatch(
      actions.getProviderTimeseries({
        provider: expandedProvider,
        metrics: providerMetrics,
        date,
        diffDate: getDiffDate(),
      }),
    );
  }, [
    date,
    dispatch,
    expandedProvider,
    getDiffDate,
    isProviderScheduleOpen,
    providerMetrics,
    selectedAgentId,
    withDiff,
  ]);

  return {
    handleFormSubmit,
    handleKeyDown,
    handleStructureChange,
    handleSearchChange,
    handleResultClick,
    onChangeBeginDate,
    onChangeEndDate,
    onChangeBeginDiffDate,
    onChangeEndDiffDate,
    onChangeActiveDay,
    onChangeDiffRange,
    handleSetWithDiff: setWithDiff,
    handleGetDiff: () => setWithDiff(true),
    handleSetActiveMetric,
    handleActiveUsersToggle,
    handleSetScheduleOpen: setIsScheduleOpen,
    handleProviderToggle,
    handleProviderMetricsApply,
    handleProviderScheduleOpen,
    handleSetSaveFilterOpen: setIsSaveFilterOpen,
    handleSaveFilter,
    handleApplySavedFilter,
    handleDeleteSavedFilter: id => dispatch(actions.deleteSavedFilter(id)),
    savedFilterParams,
    values: {
      activeDay,
      activeDiffRange,
      structure,
      filters,
      filterValue,
      searchResult,
      isLoadingSearchResult,
      date,
      diffDate,
      withDiff,
      activeMetric,
      isScheduleOpen,
      expandedProvider,
      isActiveUsersEnabled,
      activeUsers,
      providerMetrics,
      isProviderScheduleOpen,
      providerMetricError,
      isSaveFilterOpen,
    },
  };
};

export default useGeneralModel;
